import { useMemo } from "react";
import { message } from "antd";
import { useMutation, useQuery } from "@apollo/client";
import {
  GET_MANUAL_MODERATION_USER_IDS,
  SET_USER_ORDER_MODERATION,
} from "../../api/graphql/moderation";

/**
 * Which users are on manual moderation, for a list of users, and the switch
 * between manual and auto. Asked apart from the users list, so the list still
 * opens against a backend that has no moderation (`available` is false then).
 */
const useManualModeration = () => {
  const { data, error, loading, refetch } = useQuery<{
    getManualModerationUserIds: number[];
  }>(GET_MANUAL_MODERATION_USER_IDS, { fetchPolicy: "network-only" });
  const [setMode, { loading: saving }] = useMutation(SET_USER_ORDER_MODERATION);

  const manualIds = useMemo(
    () => new Set(data?.getManualModerationUserIds ?? []),
    [data],
  );

  const toggle = async (userId: number) => {
    const next = manualIds.has(userId) ? "AUTO" : "MANUAL";
    try {
      await setMode({ variables: { userId, mode: next } });
      await refetch();
      message.success(
        next === "MANUAL"
          ? "New orders of the user wait for an admin"
          : "Orders of the user go to the contractor at once",
      );
    } catch (e: any) {
      message.error(e?.message || "The moderation mode was not changed");
    }
  };

  return {
    available: !error && !loading,
    isManual: (userId: number) => manualIds.has(userId),
    toggle,
    saving,
  };
};

export default useManualModeration;
